import { type QueryFunction, type UseQueryOptions, useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { useApiError } from "~/hooks";
import peopleService from "~/services/people";
import { QueryKey } from "~/utils";

const getPartialMatchCounts: QueryFunction<Record<string, number>> = ({ queryKey: [_, __, by] }) => {
  if (typeof by !== "string") {
    throw new Error("Please provide a valid 'by' value to fetch partial match counts");
  }

  if (by === "org") {
    return peopleService.getPartialMatchCountsByOrg();
  }

  return peopleService.getPartialMatchCountsByApp();
};

type UsePartialMatchCountsQueryOptions<T> = Omit<
  UseQueryOptions<Record<string, number>, Error, T>,
  "queryKey" | "queryFn"
> & {
  params?: {
    by?: "org" | "app";
    id?: string;
  };
};

export const usePartialMatchCountsQuery = <T = Record<string, number>>({
  params: { by = "org", id } = {},
  ...queryOptions
}: UsePartialMatchCountsQueryOptions<T> = {}) => {
  const { onApiError } = useApiError();

  const query = useQuery({
    queryKey: [QueryKey.PartialMatch, "counts", by, id],
    queryFn: getPartialMatchCounts,
    ...queryOptions,
  });

  useEffect(() => {
    if (query.isError) {
      onApiError(query.error);
    }
  }, [query.isError, onApiError, query.error]);

  return query;
};
